import React, { useState, useEffect, useCallback } from "react";
import "antd/dist/reset.css";
import {
    Card,
    Table,
    Button,
    Tooltip,
    Modal,
    Spin,
    message,
    Tag,
    Typography,
    Row,
    Col,
    Statistic,
    Divider,
    Select,
    Form,
} from "antd";

import { v4 as uuidv4 } from "uuid";
import {
    EyeOutlined,
    LoadingOutlined,
    ReloadOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getAccessToken } from "../../services/authApi";
import { fieldReader } from "../../services/loanApi";

// For .env values
const WEBHOOK_URL = process.env.REACT_APP_AIAGENT_WEBHOOK_URL || "https://example.com/webhook";
const API_URL = process.env.REACT_APP_AIAGENT_API_URL || "https://example.com/api";
const INSTANCE_ID = process.env.REACT_APP_AIAGENT_INSTANCE_ID || "be11105680j";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const FIELD_ID = "CX.CHAMPAI.MVP.LOG";

const formatDate = (text) =>
    text
        ? `${dayjs(text)
              .tz("America/Los_Angeles")
              .format("YYYY-MM-DD HH:mm:ss")} PT`
        : "N/A";

const agentColor = (agent) => {
    switch ((agent || "").toUpperCase()) {
        case "MVP":
            return "blue";
        case "QC":
            return "purple";
        default:
            return "geekblue";
    }
};

const statusColor = (status) => {
    switch ((status || "").toLowerCase()) {
        case "pass":
            return "success";
        case "fail":
            return "error";
        case "info":
            return "default";
        case "warning":
            return "warning";
        default:
            return "default";
    }
};

// Parse the payload as JSON array or NDJSON
const parseLogPayload = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== "string") return [];
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === "object") return [parsed];
    } catch (_) {
        // NDJSON fallback
    }
    const items = [];
    for (const line of trimmed.split(/\r?\n/)) {
        const entry = line.trim();
        if (!entry) continue;
        try {
            const obj = JSON.parse(entry);
            if (obj && typeof obj === "object") items.push(obj);
        } catch (err) {
            // skip
        }
    }
    return items;
};

// Group records by session_id
const groupBySession = (records) => {
    const sessions = {};
    for (const rec of records) {
        if (!rec.session_id) continue;
        if (!sessions[rec.session_id]) sessions[rec.session_id] = [];
        sessions[rec.session_id].push(rec);
    }
    return sessions;
};

// Compute summary for each session
const summarizeSession = (sessionId, records) => {
    const stepsReviewed = records.filter((r) => r.status !== "info").length;
    const passed = records.filter((r) => r.status === "pass").length;
    const warning = records.filter((r) => r.status === "warning" || r.status === "review").length;
    const failed = records.filter((r) => r.status === "fail").length;
    const info = records.filter((r) => r.status === "info").length;
    let agent = "-";
    if (records[0]?.requested_by && String(records[0].requested_by).trim() !== "") {
        agent = records[0].requested_by;
    } else if (records[0]?.agent) {
        agent = records[0].agent;
    }
    const triggeredAt = records[0]?.ts;
    const completedAt = records[records.length - 1]?.ts;
    const duration = triggeredAt && completedAt
        ? dayjs(completedAt).diff(dayjs(triggeredAt), "second")
        : 0;
    // Status: fail > warning > pass
    let status = "Passed";
    // if (failed > 0) status = "Failed";
    // else if (warning > 0) status = "Warning";
    return {
        sessionId,
        agent,
        triggeredAt,
        completedAt,
        status,
        stepsReviewed,
        passed,
        warning,
        failed,
        info,
        duration,
        records,
    };
};

const getSummaryStats = (sessions) => {
    let total = 0, passed = 0, warning = 0, failed = 0, totalDuration = 0;
    Object.values(sessions).forEach((records) => {
        total++;
        const s = summarizeSession("", records);
        if (s.status === "Passed") passed++;
        else if (s.status === "Warning") warning++;
        else if (s.status === "Failed") failed++;
        totalDuration += s.duration;
    });
    return {
        total,
        passed,
        warning,
        failed,
        avgDuration: total ? Math.round(totalDuration / total) : 0,
    };
};

const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return "00:00";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const AIAgentView = () => {
    const [loanId, setLoanId] = useState("");
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sessions, setSessions] = useState({});
    const [summary, setSummary] = useState({ total: 0, passed: 0, warning: 0, failed: 0, avgDuration: 0 });
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);

    // New Session Modal
    const [newSessionModalOpen, setNewSessionModalOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(undefined);
    const [runLoading, setRunLoading] = useState(false);
    const [runSessionMode, setRunSessionMode] = useState("webhook");
    const [agentUsers, setAgentUsers] = useState([]);
    const AGENT_USERS_API = process.env.REACT_APP_AIAGENT_USERS;
    const AGENT_USERS_APIKEY = process.env.REACT_APP_AIAGENT_WEBHOOK_APIKEY;

    // Fetch agent users from API
    useEffect(() => {
        if (!AGENT_USERS_API) return;
        const fetchUsers = async () => {
            try {
                const res = await fetch(AGENT_USERS_API, {
                    headers: {
                        'x-api-key': AGENT_USERS_APIKEY || '',
                    },
                });
                if (!res.ok) throw new Error('Failed to fetch AI agent users');
                const data = await res.json();
                setAgentUsers(Array.isArray(data) ? data : []);
            } catch (err) {
                setAgentUsers([]);
            }
        };
        fetchUsers();
    }, [AGENT_USERS_API, AGENT_USERS_APIKEY]);

    // Initialize loan id from Encompass context
    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            try {
                window.elli.script.guest.create();
                window.elli.script
                    .getObject("loan")
                    .then((loanObject) =>
                        loanObject.all().then((loan) => {
                            const id = loan?.loan?.id;
                            if (id) setLoanId(id);
                        })
                    );
            } catch (err) {
                console.error("Error initializing Encompass context:", err);
            }
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);

    const fetchData = useCallback(async (id) => {
        if (!id) return;
        setIsLoading(true);
        try {
            const authObj = await window.elli.script.getObject("auth");
            const authCode = await authObj.createAuthCode();
            const token = await getAccessToken(authCode);
            if (!token) {
                throw new Error("Unable to acquire access token");
            }
            const result = await fieldReader(token, [FIELD_ID], id);
            const rawValue = result?.[FIELD_ID];
            const items = parseLogPayload(rawValue);
            setData(items);
            const grouped = groupBySession(items);
            setSessions(grouped);
            setSummary(getSummaryStats(grouped));
        } catch (err) {
            console.error("Error fetching AI agent log:", err);
            message.error(err.message || "Failed to load AI agent log");
            setData([]);
            setSessions({});
            setSummary({ total: 0, passed: 0, warning: 0, failed: 0, avgDuration: 0 });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (loanId) {
            fetchData(loanId);
        }
    }, [loanId, fetchData]);

    const handleViewDetail = (sessionId) => {
        setSelectedSession(sessionId);
        setDetailModalOpen(true);
    };

    const handleCloseDetail = () => {
        setDetailModalOpen(false);
        setSelectedSession(null);
    };


    // Table columns for session grid
    const columns = [
        {
            title: "Session ID",
            dataIndex: "sessionId",
            key: "sessionId",
            render: (text) => (
                <Text style={{ color: "#006aa9", fontWeight: 500 }}>{text}</Text>
            ),
        },
        {
            title: "Agent Name",
            dataIndex: "agent",
            key: "agent",
            render: (agent) => agent ? <Tag color={agentColor(agent)}>{agent}</Tag> : "-",
        },
        {
            title: "Start Time",
            dataIndex: "triggeredAt",
            key: "triggeredAt",
            sorter: (a, b) => new Date(a.triggeredAt || 0).getTime() - new Date(b.triggeredAt || 0).getTime(),
            defaultSortOrder: "descend",
            render: (text) => <span>{formatDate(text)}</span>,
        },
        {
            title: "End Time",
            dataIndex: "completedAt",
            key: "completedAt",
            sorter: (a, b) => new Date(a.completedAt || 0).getTime() - new Date(b.completedAt || 0).getTime(),
            defaultSortOrder: "descend",
            render: (text) => <span>{formatDate(text)}</span>,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                let icon = <CheckCircleOutlined style={{ color: "#52c41a" }} />;
                if (status === "Failed") icon = <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
                else if (status === "Warning") icon = <WarningOutlined style={{ color: "#faad14" }} />;
                return <span>{icon} <b>{status === "Passed" ? "Completed" : status}</b></span>;
            },
        },
        {
            title: "Steps Reviewed",
            dataIndex: "stepsReviewed",
            key: "stepsReviewed",
            align: "center",
        },
        {
            title: "Passed / Warning / Failed",
            key: "pwf",
            align: "center",
            render: (_, rec) => (
                <span>
                    <span style={{ color: "#52c41a", fontWeight: 500 }}>{rec.passed}</span>
                    {" / "}
                    <span style={{ color: "#faad14", fontWeight: 500 }}>{rec.warning}</span>
                    {" / "}
                    <span style={{ color: "#ff4d4f", fontWeight: 500 }}>{rec.failed}</span>
                </span>
            ),
        },
        {
            title: "Duration",
            dataIndex: "duration",
            key: "duration",
            align: "center",
            render: (seconds) => <span>{formatDuration(seconds)}</span>,
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, rec) => (
                <Tooltip title="View Detail">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(rec.sessionId)}
                    />
                </Tooltip>
            ),
        },
    ];

    // Prepare table data

    let tableData = Object.entries(sessions).map(([sessionId, records]) =>
        summarizeSession(sessionId, records)
    );
    // Sort by completedAt descending
    tableData = tableData.sort((a, b) => {
        if (!a.completedAt && !b.completedAt) return 0;
        if (!a.completedAt) return 1;
        if (!b.completedAt) return -1;
        return new Date(b.completedAt) - new Date(a.completedAt);
    });

    // Handler for Run New Session
    const handleRunNewSession = async () => {
        if (!selectedAgent || !loanId) {
            message.error("Please select an AI Agent user.");
            return;
        }
        setRunLoading(true);
        try {
            const guid = uuidv4();
            const now = new Date().toISOString();
            const payload = {
                eventId: guid,
                eventTime: now,
                eventType: "aiagent",
                meta: {
                    userId: selectedAgent,
                    resourceType: "Loan",
                    resourceId: loanId,
                    instanceId: INSTANCE_ID,
                    resourceRef: `/encompass/v3/loans/${loanId}/aiagent`,
                },
            };
            const targetUrl = runSessionMode === "api" ? API_URL : WEBHOOK_URL;
            await fetch(targetUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            message.success("AI Agent session triggered successfully.");
            setNewSessionModalOpen(false);
            setSelectedAgent(undefined);
        } catch (err) {
            message.error("Failed to trigger AI Agent session.");
        } finally {
            setRunLoading(false);
        }
    };

    return (
        <div style={{ width: "100%", padding: 18 }}>
            <Card style={{ width: "100%", margin: "0 -18px 24px -18px", borderRadius: 12, boxShadow: "0 2px 8px #f0f1f2" }} bodyStyle={{ padding: 20 }}>
                <Row gutter={24} justify="space-between" align="middle">
                    <Col>
                        <span style={{ color: "#006aa9", fontWeight: 600, fontSize: 22 }}>
                            AI Review Session Runs
                        </span>
                        <span style={{ color: "#888", marginLeft: 16, fontSize: 15 }}>
                            All AI review session runs for this loan. Click a row to view detailed analysis.
                        </span>
                    </Col>
                    <Col>
                        <div style={{ display: "flex", gap: 12 }}>
                            <Tooltip title="Refresh">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => fetchData(loanId)}
                                    disabled={!loanId || isLoading}
                                    style={{ borderRadius: 6 }}
                                >Refresh</Button>
                            </Tooltip>
                             <Button
                                type="primary"
                                style={{ borderRadius: 6, fontWeight: 600 }}
                                icon={<span style={{ fontSize: 18, fontWeight: 700, marginRight: 4 }}>+</span>}
                                onClick={() => setNewSessionModalOpen(true)}
                            >Run New Session</Button>
                        </div>
                    </Col>
                </Row>
                            {/* Run New Session Modal */}
                            <Modal
                                title={
                                    <span style={{ fontWeight: 700, fontSize: 18 }}>
                                        Run New AI Agent Session
                                    </span>
                                }
                                open={newSessionModalOpen}
                                onCancel={() => { setNewSessionModalOpen(false); setSelectedAgent(undefined); }}
                                footer={null}
                                width={400}
                                destroyOnClose
                            >
                                <Form layout="vertical">
                                    <Form.Item label="AI Agent User" required>
                                        <Select
                                            placeholder="Select AI Agent User"
                                            value={selectedAgent}
                                            onChange={setSelectedAgent}
                                            loading={!agentUsers.length}
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.children || '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {agentUsers.map(user => (
                                                <Select.Option key={user.userId} value={user.userId}>
                                                    {user.userName} ({user.userId})
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item>
                                        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}> 
                                            {/* space-between */}
                                            {/* <Button
                                                type="primary"
                                                disabled={true}
                                                // loading={runLoading}
                                                onClick={() => { setRunSessionMode("webhook"); handleRunNewSession(); }}
                                                style={{ fontWeight: 600, width: "48%" }}
                                            >
                                                Run (Webhook)
                                            </Button> */}
                                            <Button
                                                type="primary"
                                                loading={runLoading}
                                                onClick={() => { setRunSessionMode("api"); handleRunNewSession(); }}
                                                style={{
                                                    fontWeight: 600,
                                                    width: "48%",
                                                    backgroundColor: "#006aa9",
                                                    borderColor: "#006aa9",
                                                    color: "whitesmoke"
                                                }}
                                            >
                                                Run
                                            </Button>
                                        </div>
                                    </Form.Item>
                                </Form>
                            </Modal>
                <Divider style={{ margin: "18px 0 12px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, width: "100%" }}>
                    {/* Total Sessions */}
                    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                        <span style={{ background: "#edeaff", borderRadius: "50%", padding: 10, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 14 }}>
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="4" y="3" width="14" height="16" rx="3" fill="#edeaff" stroke="#a084e8" strokeWidth="2"/><rect x="7" y="7" width="8" height="2" rx="1" fill="#a084e8"/><rect x="7" y="11" width="8" height="2" rx="1" fill="#a084e8"/></svg>
                        </span>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{summary.total}</span>
                            <span style={{ color: "#222", fontSize: 14, fontWeight: 500 }}>Total Sessions</span>
                        </div>
                    </div>
                    <div style={{ width: 1, background: "#f0f0f0", margin: "0 18px" }} />
                    {/* Passed */}
                    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                        <span style={{ background: "#e6fbe6", borderRadius: "50%", padding: 10, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 14 }}>
                            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 22 }} />
                        </span>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{summary.passed}</span>
                            <span style={{ color: "#222", fontSize: 14, fontWeight: 500 }}>Completed</span>
                        </div>
                    </div>
                    <div style={{ width: 1, background: "#f0f0f0", margin: "0 18px" }} />
                    {/* Warning */}
                    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                        <span style={{ background: "#fff7e6", borderRadius: "50%", padding: 10, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 14 }}>
                            <SyncOutlined style={{ color: "#1890ff", fontSize: 22 }} />
                        </span>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{summary.warning}</span>
                            <span style={{ color: "#222", fontSize: 14, fontWeight: 500 }}>Processing</span>
                        </div>
                    </div>
                    <div style={{ width: 1, background: "#f0f0f0", margin: "0 18px" }} />
                    {/* Failed */}
                    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                        <span style={{ background: "#ffeaea", borderRadius: "50%", padding: 10, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 14 }}>
                            <ClockCircleOutlined style={{ color: "#ff4d4f", fontSize: 22 }} />
                        </span>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{summary.failed}</span>
                            <span style={{ color: "#222", fontSize: 14, fontWeight: 500 }}>Pending</span>
                        </div>
                    </div>
                    <div style={{ width: 1, background: "#f0f0f0", margin: "0 18px" }} />
                    {/* Avg. Duration */}
                    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                        <span style={{ background: "#e6f0ff", borderRadius: "50%", padding: 10, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 14 }}>
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="9" stroke="#1677ff" strokeWidth="2" fill="#e6f0ff" /><path d="M11 6v5l3 3" stroke="#1677ff" strokeWidth="2" strokeLinecap="round" /></svg>
                        </span>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{formatDuration(summary.avgDuration)}</span>
                            <span style={{ color: "#222", fontSize: 14, fontWeight: 500 }}>Avg. Duration <span style={{ color: "#888", fontSize: 12, fontWeight: 400 }}>mm:ss</span></span>
                        </div>
                    </div>
                </div>
            </Card>
            <Card style={{ width: "100%", margin: "0 -18px", borderRadius: 12, boxShadow: "0 2px 8px #f0f1f2" }} bodyStyle={{ padding: 0 }}>
                {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                        <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />}
                        />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        rowKey={(record) => record.sessionId}
                        bordered
                        size="middle"
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        scroll={{ x: 1200 }}
                        locale={{
                            emptyText: loanId
                                ? "No AI agent session runs found for this loan."
                                : "Loading loan context...",
                        }}
                    />
                )}
            </Card>

            {/* Detail Modal (popup content to be implemented as per next instruction) */}
            <Modal
                title={null}
                open={detailModalOpen}
                onCancel={handleCloseDetail}
                footer={[
                    <Button key="close" onClick={handleCloseDetail}>
                        Close
                    </Button>,
                ]}
                width={1100}
                destroyOnClose
                bodyStyle={{ background: "#fafbfc", padding: 0 }}
            >
                {selectedSession && (() => {
                    const records = sessions[selectedSession] || [];
                    if (!records.length) return null;
                    // Sort by timestamp
                    const sorted = [...records].sort((a, b) => new Date(a.ts) - new Date(b.ts));
                    // Summary counts
                    // Treat 'review' as 'warning' for dashboard and timeline
                    const passed = sorted.filter(r => r.status === "pass").length;
                    const warning = sorted.filter(r => r.status === "warning" || r.status === "review").length;
                    const failed = sorted.filter(r => r.status === "fail").length;
                    const stepsReviewed = sorted.filter(r => r.status !== "info").length;
                    const reviewTimeSec = Math.max(1, Math.round((new Date(sorted[sorted.length-1].ts) - new Date(sorted[0].ts))/1000));
                    const reviewedAt = sorted[sorted.length-1]?.ts;
                    // Timeline steps (exclude info)
                    const timeline = sorted.filter(r => r.status !== "info");
                    // AI Recommendation (first info)
                    const aiRec = sorted.find(r => r.status === "info");
                    // Status helpers
                    const statusIcon = (status) => {
                        if (status === "pass") return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 20 }} />;
                        if (status === "fail") return <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 20 }} />;
                        if (status === "warning" || status === "review") return <WarningOutlined style={{ color: "#faad14", fontSize: 20 }} />;
                        return null;
                    };
                    const statusColor = (status) => {
                        if (status === "pass") return "#e6fbe6";
                        if (status === "fail") return "#ffeaea";
                        if (status === "warning" || status === "review") return "#fff7e6";
                        return "#f5f5f5";
                    };
                    const statusText = (status) => {
                        if (status === "pass") return "Passed";
                        if (status === "fail") return "Failed";
                        if (status === "warning" || status === "review") return "Warning";
                        return status;
                    };
                    // Format review time
                    const reviewTimeStr = reviewTimeSec < 60 ? `~1 min` : `~${Math.round(reviewTimeSec/60)} min`;
                    // Format date/time
                    const formatTime = (ts) => dayjs(ts).format("h:mm:ss A");
                    const formatDate = (ts) => dayjs(ts).format("MMM DD, YYYY");
                    // Main render
                    return (
                        <div style={{ background: "#fafbfc", borderRadius: 12, overflow: "hidden" }}>
                            {/* Header summary */}
                            <div style={{ padding: 28, borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <div>
                                        <span style={{ fontWeight: 700, fontSize: 22, color: "#1a1a1a", display: "flex", alignItems: "center" }}>
                                            <span style={{ marginRight: 10, color: "#a084e8" }}>
                                                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="4" y="3" width="14" height="16" rx="3" fill="#edeaff" stroke="#a084e8" strokeWidth="2"/><rect x="7" y="7" width="8" height="2" rx="1" fill="#a084e8"/><rect x="7" y="11" width="8" height="2" rx="1" fill="#a084e8"/></svg>
                                            </span>
                                            AI Work Summary
                                        </span>
                                        <div style={{ color: "#888", fontSize: 15, marginTop: 2 }}>
                                            AI agent reviewed this loan and analyzed key workflow checkpoints.
                                        </div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                        <div style={{ color: "#a084e8", fontWeight: 600, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ marginRight: 6 }}><circle cx="10" cy="10" r="8" stroke="#a084e8" strokeWidth="2" fill="#edeaff" /><path d="M10 6v4l2 2" stroke="#a084e8" strokeWidth="2" strokeLinecap="round" /></svg>
                                            Reviewed on
                                        </div>
                                        <div style={{ color: "#222", fontWeight: 500, fontSize: 15 }}>{formatDate(reviewedAt)} {formatTime(reviewedAt)} PT</div>
                                    </div>
                                </div>
                                {/* Summary row */}
                                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28 }}>
                                    {/* Passed */}
                                    <div style={{ background: "#e6fbe6", borderRadius: 12, flex: 1, marginRight: 16, padding: 16, display: "flex", alignItems: "center" }}>
                                        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 28, marginRight: 14 }} />
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{passed}</div>
                                            <div style={{ color: "#222", fontSize: 15, fontWeight: 500 }}>Checks Passed</div>
                                            <div style={{ color: "#52c41a", fontSize: 13 }}>All good</div>
                                        </div>
                                    </div>
                                    {/* Warning */}
                                    <div style={{ background: "#fff7e6", borderRadius: 12, flex: 1, marginRight: 16, padding: 16, display: "flex", alignItems: "center" }}>
                                        <WarningOutlined style={{ color: "#faad14", fontSize: 28, marginRight: 14 }} />
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{warning}</div>
                                            <div style={{ color: "#222", fontSize: 15, fontWeight: 500 }}>Warning</div>
                                            <div style={{ color: "#faad14", fontSize: 13 }}>Needs attention</div>
                                        </div>
                                    </div>
                                    {/* Failed */}
                                    <div style={{ background: "#ffeaea", borderRadius: 12, flex: 1, marginRight: 16, padding: 16, display: "flex", alignItems: "center" }}>
                                        <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 28, marginRight: 14 }} />
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{failed}</div>
                                            <div style={{ color: "#222", fontSize: 15, fontWeight: 500 }}>Failed</div>
                                            <div style={{ color: "#ff4d4f", fontSize: 13 }}>Action required</div>
                                        </div>
                                    </div>
                                    {/* Steps Reviewed */}
                                    <div style={{ background: "#edeaff", borderRadius: 12, flex: 1, marginRight: 16, padding: 16, display: "flex", alignItems: "center" }}>
                                        <svg width="28" height="28" viewBox="0 0 22 22" fill="none" style={{ marginRight: 18 }}><rect x="4" y="3" width="14" height="16" rx="3" fill="#edeaff" stroke="#a084e8" strokeWidth="2"/><rect x="7" y="7" width="8" height="2" rx="1" fill="#a084e8"/><rect x="7" y="11" width="8" height="2" rx="1" fill="#a084e8"/></svg>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{stepsReviewed}</div>
                                            <div style={{ color: "#222", fontSize: 15, fontWeight: 500 }}>Steps Reviewed</div>
                                            <div style={{ color: "#a084e8", fontSize: 13 }}>By AI agent</div>
                                        </div>
                                    </div>
                                    {/* Review Time */}
                                    <div style={{ background: "#e6f0ff", borderRadius: 12, flex: 1, padding: 16, display: "flex", alignItems: "center" }}>
                                        <svg width="28" height="28" viewBox="0 0 22 22" fill="none" style={{ marginRight: 18 }}><circle cx="11" cy="11" r="9" stroke="#1677ff" strokeWidth="2" fill="#e6f0ff" /><path d="M11 6v5l3 3" stroke="#1677ff" strokeWidth="2" strokeLinecap="round" /></svg>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>{reviewTimeStr}</div>
                                            <div style={{ color: "#222", fontSize: 15, fontWeight: 500 }}>Review Time</div>
                                            <div style={{ color: "#1677ff", fontSize: 13 }}>Total analysis time</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Timeline */}
                            <div style={{ padding: 28, paddingBottom: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 18, color: "#1a1a1a", marginBottom: 2 }}>Review Timeline</div>
                                <div style={{ color: "#888", fontSize: 15, marginBottom: 18 }}>Step-by-step analysis performed by the AI agent</div>
                                <div style={{ position: "relative", marginLeft: 18, marginBottom: 8 }}>
                                    {/* Timeline vertical line */}
                                    <div style={{ position: "absolute", left: 8, top: 0, bottom: 0, width: 2, background: "#e0e0e0", zIndex: 0 }} />
                                    {timeline.map((step, idx) => {
                                        const isLast = idx === timeline.length - 1;
                                        return (
                                            <div key={idx} style={{ display: "flex", alignItems: "flex-start", marginBottom: isLast ? 0 : 28, position: "relative", zIndex: 1 }}>
                                                {/* Time/Date and icon */}
                                                <div style={{ width: 90, textAlign: "right", marginRight: 18 }}>
                                                    <div style={{ color: "#222", fontWeight: 600, fontSize: 15 }}>{formatTime(step.ts)}</div>
                                                    <div style={{ color: "#888", fontSize: 13 }}>{formatDate(step.ts)}</div>
                                                </div>
                                                <div style={{ marginRight: 18, marginTop: 2, zIndex: 2 }}>
                                                    <span style={{ background: statusColor(step.status), borderRadius: "50%", padding: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        {statusIcon(step.status)}
                                                    </span>
                                                </div>
                                                {/* Step card */}
                                                <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px #f0f1f2", padding: 18, flex: 1, minWidth: 0, borderLeft: `4px solid ${step.status === "pass" ? "#52c41a" : step.status === "fail" ? "#ff4d4f" : "#faad14"}` }}>
                                                    <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                                                        <span style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a", marginRight: 10 }}>{step.step && step.step.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                                                        <span style={{ background: statusColor(step.status), color: step.status === "pass" ? "#52c41a" : step.status === "fail" ? "#ff4d4f" : "#faad14", fontWeight: 600, fontSize: 13, borderRadius: 8, padding: "2px 10px", marginLeft: 6, textTransform: "capitalize" }}>{statusText(step.status)}</span>
                                                    </div>
                                                    <div style={{ color: "#222", fontSize: 15, marginBottom: 2 }}>{step.detail}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* AI Recommendation */}
                            {aiRec && (
                                <div style={{ background: "#f5f8ff", borderRadius: 12, margin: 28, marginTop: 0, padding: 22, display: "flex", alignItems: "flex-start" }}>
                                    <span style={{ background: "#e6f0ff", borderRadius: "50%", padding: 12, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 18, marginTop: 2 }}>
                                        {/* Clipboard with checkmark icon */}
                                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                            <rect x="8" y="6" width="16" height="20" rx="4" fill="#e6f0ff" stroke="#1677ff" strokeWidth="2"/>
                                            <rect x="12" y="4" width="8" height="4" rx="2" fill="#1677ff"/>
                                            <path d="M13 18l3 3 5-5" stroke="#52c41a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 16, color: "#1677ff", marginBottom: 2 }}>
                                            {(() => {
                                                // Try to get agent user id and requested_by from the first record
                                                const requestedBy = sorted[0]?.requested_by || "-";
                                                return `AI Agent (${requestedBy}) Summary`;
                                            })()}
                                        </div>
                                        <ul style={{ color: "#222", fontSize: 15, margin: 0, paddingLeft: 18 }}>
                                            {aiRec.detail.split(';').map((item, idx) => {
                                                const text = item.trim();
                                                if (!text) return null;
                                                return <li key={idx} style={{ listStyle: 'none', marginBottom: 4 }}><span style={{ color: '#1677ff', fontWeight: 700, marginRight: 6 }}>*</span>{text}</li>;
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
            </Modal>
        </div>
    );
};

export default AIAgentView;
